import { activeLocale } from "./catalogue.js";
import type { LanguageLocale } from "./language.js";

/**
 * Every date a reader sees, written the way they read dates.
 *
 * ==========================================================================================
 * ONE PLACE, AND NO LOCALE PARAMETER
 * ==========================================================================================
 *
 * Before this feature, displayed dates were inconsistent three ways: `en-GB` hardcoded in six
 * places, the browser default in five more, and nothing saying which was intended. FR-035 exists
 * because two dates disagreeing on the same screen is a defect that has actually shipped here.
 *
 * None of these takes a locale argument, and that is the same trade `money.ts` made and wrote down:
 * a parameter is a thing a caller forgets, and a caller who forgets gets the household's format
 * instead of the reader's — which looks right in Amsterdam and is wrong everywhere else.
 *
 * ==========================================================================================
 * WHAT IS NOT HERE, AND MUST NOT BE MOVED HERE
 * ==========================================================================================
 *
 * Three places in this repository ask `Intl` for `en-CA`, and none of them is display:
 *
 *   apps/api/src/adapters/services/finnhub-price-adapter.ts   a URL query parameter
 *   apps/api/src/jobs/index-valuations.ts                     a CBS query parameter
 *   apps/app/src/components/dashboard/freshness-panel.tsx     a key fed to Date.parse
 *
 * `en-CA` renders ISO order, which is the cheapest `YYYY-MM-DD` out of `Intl` without assembling
 * parts by hand. NONE IS READ BY A PERSON, and routing any of them through this module breaks its
 * consumer SILENTLY — the jobs would query a malformed date and log a failure nobody reads, and the
 * freshness panel would compute NaN days and say "Updated today" forever.
 *
 * `machine-dates.test.ts` guards this by asserting those modules import no language resolver, rather
 * than by asserting the string `"en-CA"` — a well-meaning sweep renames a tag to a constant before it
 * breaks anything, and a literal check passes over the rename.
 */

/** How much of a date to show. `long` names the month; `short` abbreviates it for an axis. */
export type DateStyle = "long" | "short";

/**
 * ONE SOURCE FOR THE ACTIVE LOCALE — see `activeLocale` in `catalogue.ts`.
 *
 * This used to re-derive it from the preference store, which made the dates a SECOND derivation of a
 * fact the catalogue already held. They agreed in the browser and disagreed everywhere else: a unit
 * test that installed the English catalogue still got Dutch dates, because a Node process has no
 * `navigator` and the resolver fell back.
 */
function currentLocale(): LanguageLocale {
  return activeLocale();
}

const FORMATTERS = new Map<string, Intl.DateTimeFormat>();

/** One `Intl.DateTimeFormat` per locale and shape. Constructing one is expensive and this is per cell. */
function formatterFor(
  locale: LanguageLocale,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  const cached = FORMATTERS.get(key);
  if (cached !== undefined) return cached;
  const made = new Intl.DateTimeFormat(locale, options);
  FORMATTERS.set(key, made);
  return made;
}

/**
 * A calendar day, as `17 August 2026` or `17 augustus 2026`.
 *
 * `nl-NL` and `en-GB` share day-month-year order, so the two languages differ in the month's WORD
 * and not in the shape of the date. That is a property of the `en-GB` decision rather than a
 * coincidence, and it is what makes a side-by-side comparison of one screen a one-variable diff.
 *
 * READ AS UTC, because a `YYYY-MM-DD` from the database is a calendar day and not an instant.
 * Parsing it in local time moves it a day backwards for anyone west of Greenwich.
 */
export function formatDate(iso: string, style: DateStyle = "long"): string {
  const at = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(at.getTime())) return iso;

  return formatterFor(currentLocale(), {
    day: "numeric",
    month: style === "long" ? "long" : "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(at);
}

/** A month, as `August 2026` or `augustus 2026`. `month` is 1-based, as every caller's data is. */
export function formatMonth(year: number, month: number, style: DateStyle = "long"): string {
  const at = new Date(Date.UTC(year, month - 1, 1));
  if (Number.isNaN(at.getTime())) return `${year}-${month}`;

  return formatterFor(currentLocale(), {
    month: style === "long" ? "long" : "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(at);
}

/** A month with no year: `Aug`. For a dense axis where the year is added only when it changes. */
export function formatMonthOnly(year: number, month: number): string {
  const at = new Date(Date.UTC(year, month - 1, 1));
  if (Number.isNaN(at.getTime())) return String(month);

  return formatterFor(currentLocale(), { month: "short", timeZone: "UTC" }).format(at);
}

/** A day with no year: `17 Aug`. For an axis whose window is short enough not to need one. */
export function formatDayAndMonth(iso: string): string {
  const at = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(at.getTime())) return iso;

  return formatterFor(currentLocale(), {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(at);
}

/** An instant with its time, for a feed row: `17 August 2026 at 14:30`. */
export function formatDateTime(at: Date): string {
  if (Number.isNaN(at.getTime())) return "";

  return formatterFor(currentLocale(), {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(at);
}
